import { List, ListItem, makeStyles } from "@material-ui/core";
import { attributes, Events } from "@type-r/models";
import { Linked, useEvent, useIO, useLinked, useModel } from "@type-r/react";
import { asDate, colors, Flex, Link, Loading, PopupIODialog, PrimaryButton } from "lqx-react";
import React from "react";
import { DebthSummary } from "../api";
import CloudDownloadIcon from '@material-ui/icons/CloudDownload';
import { CircularProgress } from '@material-ui/core';
import { localUserInfo } from "api/localUserInfo";

export const SocEmailButton = ({ customerDepth }: {
    customerDepth: DebthSummary
}) => {

    const $isOpen = useLinked(false);

    return (
        <>
            <PrimaryButton color="blue" onClick={$isOpen.true} >Send Email</PrimaryButton>
            {$isOpen.value ? <SocSendEmailDialog $isOpen={$isOpen} customerDebth={customerDepth} /> : null}
        </>
    )
};

const SocReport = attributes({
    id: Number,
    fileName: String,
    filePath: String,
    fileType: String,
    document_type: String
});

const LatestReportSent = attributes({
    sentDate: Date,
    file: SocReport
});

export const SocLatestStatementSent = ({ customerDepth }: {
    customerDepth: DebthSummary
}) => {

    const latestSent = useModel(LatestReportSent);

    const loadLatestReportSent = async () => {
        var resp = await customerDepth.getLatestReportSent();
        latestSent.set(resp);
    }

    useEvent(Events, "Soc:statementEmailSent", loadLatestReportSent);

    const isReady = useIO(loadLatestReportSent, []);

    const download = async () => {

        let url = `/api/v1/download/${latestSent.file.id}`;

        const response = await fetch(url, {
            method: 'GET',
            headers: new Headers({
                "Authorization": "Bearer " + localUserInfo.token
            })
        });

        const blob = await response.blob();

        const dataUrl = window.URL.createObjectURL(blob),
            a = document.createElement('a');

        a.href = dataUrl;
        a.download = latestSent.file.fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
    }

    return (
        isReady
            ? latestSent.file && latestSent.file.fileName
                ? <p style={{ margin: 0 }}>{asDate(latestSent.sentDate)} <CloudDownloadIcon /> <Link onClick={() => download()}>Last Sent Copy</Link> </p>
                : <p style={{ margin: 0 }}>SOA has not been sent</p>
            : <SmallLoading />
    );
};

const SocSendEmailDialog = ({ $isOpen, customerDebth }: {
    $isOpen: Linked<boolean>
    customerDebth: DebthSummary
}) => {

    const styles = useStyles();
    const SendEmail = async () => {
        await customerDebth.sendEmail();
        Events.trigger("Soc:statementEmailSent");
    };

    const isReady = useIO(async () => {
        await customerDebth.relationship.cpStatement.fetch();
    }, []);

    const isEmpty = (arr: any[]) => arr && !arr.length;

    const soaLimits = {
        currentRowCount : customerDebth.count 
                        + customerDebth.overdueCount 
                        + customerDebth.disputedCount
                        + customerDebth.due2weeksCount
                        + customerDebth.due2plusWeeksCount ,
        aproximateLimit : 70_000 ,
        fileSizeLimit : 8,
        isOverLimit : function(){ 
            return this.currentRowCount >= this.aproximateLimit;
         } 
    };
    return (
        <PopupIODialog
            title="Send email"
            $isOpen={$isOpen}
            actionLabel="Send"
            success="Success!"
            doIO={SendEmail}
            hideTitleOnSuccess
            disabled={!isReady || isEmpty(customerDebth.relationship.cpStatement.receiptMailAddress)}
        >
            {
                !isReady
                    ? <DialogLoading></DialogLoading>
                    : isEmpty(customerDebth.relationship.cpStatement.receiptMailAddress)
                        ? <> No recipient set on Customer Profile - Statement of Account. </>
                        : <>
                            Do you want to send a report via email to the following recipients?
                            <List>
                                {customerDebth.relationship.cpStatement.receiptMailAddress.map(recipientMail =>
                                    <ListItem key={recipientMail as any}><b>{recipientMail}</b></ListItem>
                                )}
                            </List>
                            {soaLimits.isOverLimit() ? <p className={styles.warning} >Your report will contain over {soaLimits.aproximateLimit} line items and may take some time to process. <b>If your report exceeds {soaLimits.fileSizeLimit} mb, the system will not be able to send the report.</b></p> : <></>}
                        </>
            }
        </PopupIODialog>
    );
};

const useStyles = makeStyles({
    warning:{
        color: colors.orange,
        fontSize: "0.9em"
    }
});

export const SmallLoading = () => {
    return (
        <Flex center>
            <CircularProgress style={{height: 22,width: 22}}/>
        </Flex>
    )
}

export const DialogLoading = () => {
    return (
        <div style={{ height: 41 }}><Loading /></div> // prevent overflow flicker for rotating svg
    );
}

export const SocExportButton = ({ customerDepth }: {
    customerDepth: DebthSummary
}) => {

    const $isOpen = useLinked(false);

    return (
        <>
            <PrimaryButton color="blue" onClick={$isOpen.true} >Export</PrimaryButton>
            {$isOpen.value ? <SocExportDialog $isOpen={$isOpen} customerDebth={customerDepth} /> : null}
        </>
    )
};

const SocExportDialog = ({ $isOpen, customerDebth }: {
    $isOpen: Linked<boolean>
    customerDebth: DebthSummary
}) => {

    const Export = async () => await customerDebth.export();

    return (
        <PopupIODialog
            title="Export"
            $isOpen={$isOpen}
            actionLabel="Yes"
            success="Success!"
            doIO={Export}
            hideTitleOnSuccess
        >
            Do you want to generate a report?
        </PopupIODialog>
    );
};
